import { intervalToDuration } from "date-fns/intervalToDuration";
import { isBefore } from "date-fns/isBefore";
import { startOfDay } from "date-fns/startOfDay";
import type { Duration } from "date-fns";

import AppContent, { ContentNS } from "./content";

export function getJourneyDate(date: string) {
  const d = new Date(date);
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "full" }).format(d);
}

/*
  Extracts the locale-correct date pieces (weekday, day, month, year) for any
  language via Intl — no per-locale imports or maps needed, so it scales to
  every client locale. The i18next "localizedDate" formatter feeds these into a
  lang-pack template (e.g. "trip-date-format"), so each client/locale defines
  its own arrangement and literal text — the Spanish "de", separators, the
  abbreviated-month period — instead of hardcoding a format here.
*/
export function localizedDateParts(date: Date, lng: string) {
  const parts = new Intl.DateTimeFormat(lng, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return {
    weekday: get("weekday"),
    day: get("day"),
    // Strip any locale-provided trailing dot so the template alone controls
    // punctuation (avoids e.g. "jun.." when a template appends its own period).
    month: get("month").replace(/\.$/, ""),
    year: get("year"),
  };
}

export const getAge = (birthDate: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

/*
  This function is used to calculate the duration between now and the departure date of the flight.
  Clients want to see days difference only, so for example for dates:
    2023-08-18 00:05 and 2023-08-17 23:55
  the difference will be 1 day.
  The date of departure comes from the backend in format "yyyy-MM-dd hh:mm".
  When we convert it to Date object it will be in local timezone.
  When we calculate now by instantiating new Date() it will be in local timezone as well.
  So there is no adjustment to timezones needed here.
*/
export const durationTillDate = (
  date: Date,
  now: Date = new Date()
): Duration | null => {
  if (
    isBefore(startOfDay(date), startOfDay(now)) ||
    !date ||
    isNaN(date.getTime())
  ) {
    return null;
  }

  return intervalToDuration({
    start: startOfDay(now),
    end: startOfDay(date),
  });
};

export function formattedTimeTillJourney(duration: Duration | null): string {
  const content = AppContent.shared;
  const options = { ns: ContentNS.time };

  // Return empty string for invalid/past dates
  if (duration === null) {
    return "";
  }

  // If no months and no days remaining, show "today"
  if (
    (!duration.years || duration.years === 0) &&
    (!duration.months || duration.months === 0) &&
    !duration.days
  ) {
    return `${content.t("today", { ...options })}`;
  }

  // If no months and exactly 1 day remaining, show "tomorrow"
  if (
    (!duration.years || duration.years === 0) &&
    (!duration.months || duration.months === 0) &&
    duration.days === 1
  ) {
    return `${content.t("tomorrow", { ...options })}`;
  }

  let result = "";

  // If duration is 1+ years, set result to "in X years"
  if (duration.years) {
    result = `${content.t("in-time", {
      ...options,
      time: content.t("yearWithCount", {
        ...options,
        count: duration.years,
      }),
    })}`;
  }

  // If 2+ months or (1 month + 15+ days), append/set months to result
  // Appends to years if already set, otherwise starts with "in X months"
  if (
    (duration.months !== undefined && duration?.months >= 2) ||
    (duration.months === 1 && duration.days !== undefined && duration.days > 15)
  ) {
    const monthsCount = content.t("monthWithCount", {
      ...options,
      count: duration.months || 0,
    });
    result = result
      ? `${result} ${monthsCount}`
      : `${content.t("in-time", {
          ...options,
          time: monthsCount,
        })}`;
  } else if (
    (duration.months === 1 &&
      duration.days !== undefined &&
      duration.days < 20) ||
    (duration.months === 0 && duration.days && duration.days > 14)
  ) {
    // If 1 month with <20 days or 14+ days (no months), append/set weeks to result
    // Uses approximate weeks calculation for better readability
    const approximateWeeksInMonth = 4.3;
    const weeks = Math.floor(
      (duration.months || 0) * approximateWeeksInMonth +
        (duration.days || 0) / 7
    );
    const weeksCount = content.t("weekWithCount", {
      ...options,
      count: weeks,
    });
    result = result
      ? `${result} ${weeksCount}`
      : `${content.t("in-time", {
          ...options,
          time: weeksCount,
        })}`;
  } else if (duration.days) {
    // Finally, append/set days to result
    const daysCount = content.t("dayWithCount", {
      ...options,
      count: duration.days,
    });
    result = result
      ? `${result} ${daysCount}`
      : `${content.t("in-time", {
          ...options,
          time: daysCount,
        })}`;
  }

  return result;
}
